/**
 * The five approved interaction states, plus the boundary that selects between them.
 *
 * Every screen renders one of these rather than inventing its own. Five divergent
 * interpretations of "empty" is how an interface starts telling users different things
 * about the same situation.
 *
 * The distinctions that matter most:
 *
 * - **Empty is not error.** Nothing matched is a normal outcome with a normal next step.
 * - **Empty is not no-access.** One means try different filters, the other means ask for
 *   access. Conflating them sends people to the wrong place.
 * - **Stale is not failure.** Stale data is still data; it is shown *alongside* the notice
 *   rather than replaced by it, because hiding it helps nobody.
 */

import { ButtonField, Card, Icon2, Typography } from '@wk/components-v3-react16'
import type { ReactNode } from 'react'

import { ApiError } from '../api/client'

interface StateProps {
  readonly title: string
  readonly description?: string
  readonly action?: ReactNode
}

/**
 * Loading.
 *
 * Reserves the eventual layout so content does not jump in underneath the user's cursor,
 * and announces politely so a screen-reader user is told something is happening.
 */
export function LoadingState({
  label = 'Loading',
}: {
  readonly label?: string
}) {
  return (
    <div
      className="devex-state devex-state--loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="loading-state"
    >
      <span className="devex-state__spinner" aria-hidden="true" />
      <Typography>{label}</Typography>
    </div>
  )
}

/** Nothing matched. Explains why, and offers the action that would change it. */
export function EmptyState({ title, description, action }: StateProps) {
  return (
    <Card>
      <div className="devex-state devex-state--empty" data-testid="empty-state">
        <Icon2 name="wk-icon-inbox" flowClass="devex-icon--large" />
        <Typography>
          <h2 className="devex-state__title">{title}</h2>
        </Typography>
        {description !== undefined && (
          <Typography>
            <p className="devex-state__description">{description}</p>
          </Typography>
        )}
        {action}
      </div>
    </Card>
  )
}

/**
 * The user is not permitted to see this.
 *
 * Says nothing about what the data would have been, how much of it exists, or who can see
 * it. A no-access state that leaks the shape of the restricted data has not restricted it.
 */
export function NoAccessState({
  title = 'You do not have access to this',
  description = 'Your role does not include this area. If you need it, ask your manager or the governance team.',
}: Partial<StateProps>) {
  return (
    <Card>
      <div
        className="devex-state devex-state--no-access"
        data-testid="no-access-state"
      >
        <Icon2 name="wk-icon-lock" flowClass="devex-icon--large" />
        <Typography>
          <h2 className="devex-state__title">{title}</h2>
        </Typography>
        <Typography>
          <p className="devex-state__description">{description}</p>
        </Typography>
      </div>
    </Card>
  )
}

/** Something failed. Explains it in plain language and offers a retry when one would help. */
export function ErrorState({
  error,
  onRetry,
}: {
  readonly error: unknown
  readonly onRetry?: () => void
}) {
  const apiError = error instanceof ApiError ? error : undefined
  const message =
    apiError?.message ??
    (error instanceof Error
      ? error.message
      : 'The request could not be completed.')
  const retryable = apiError === undefined || apiError.isRetryable

  return (
    <Card>
      <div
        className="devex-state devex-state--error"
        role="alert"
        data-testid="error-state"
      >
        <Icon2 name="wk-icon-caution" flowClass="devex-icon--large" />
        <Typography>
          <h2 className="devex-state__title">Something went wrong</h2>
        </Typography>
        <Typography>
          <p className="devex-state__description">{message}</p>
        </Typography>
        {apiError?.correlationId !== undefined && (
          <Typography>
            <p className="devex-state__reference">
              Quote reference <code>{apiError.correlationId}</code> when
              reporting this.
            </p>
          </Typography>
        )}
        {retryable && onRetry !== undefined && (
          <ButtonField type="secondary">
            <button type="button" onClick={onRetry}>
              Try again
            </button>
          </ButtonField>
        )}
      </div>
    </Card>
  )
}

/**
 * The data is older than expected.
 *
 * Rendered above the data, never instead of it. Removing stale data would leave the user
 * with nothing when they could have had something, clearly labelled.
 */
export function StaleDataNotice({
  asOf,
  reason,
}: {
  readonly asOf: string
  readonly reason?: string
}) {
  return (
    <div
      className="devex-stale-notice"
      role="status"
      data-testid="stale-data-notice"
    >
      <Icon2 name="wk-icon-clock" flowClass="devex-icon--small" />
      <Typography>
        <span>
          This data is from {asOf} and may be out of date.
          {reason !== undefined ? ` ${reason}` : ''}
        </span>
      </Typography>
    </div>
  )
}

/**
 * Visible data freshness.
 *
 * Text, not a tooltip. A figure whose age is only discoverable by hovering is a figure
 * most people will read as current.
 *
 * When the source timestamp could not be parsed, the label says so rather than presenting
 * the ingestion time as though the provider had reported it.
 */
export function LastRefreshed({
  asOfUtc,
  isSourceTime = true,
}: {
  readonly asOfUtc: string | null | undefined
  readonly isSourceTime?: boolean
}) {
  if (asOfUtc === null || asOfUtc === undefined) {
    return (
      <span className="devex-freshness" data-testid="last-refreshed">
        Data freshness unknown
      </span>
    )
  }
  const when = new Date(asOfUtc)
  const rendered = Number.isNaN(when.getTime())
    ? asOfUtc
    : when.toLocaleString()
  return (
    <span className="devex-freshness" data-testid="last-refreshed">
      {isSourceTime ? 'Data as of' : 'Loaded'} {rendered}
      {!isSourceTime && (
        <>
          {' '}
          <span className="devex-freshness__qualifier">
            (the provider did not report a usable time)
          </span>
        </>
      )}
    </span>
  )
}

export interface AsyncBoundaryProps<T> {
  readonly isPending: boolean
  readonly error: unknown
  readonly data: T | undefined
  readonly onRetry?: () => void
  readonly loadingLabel?: string
  /** Rendered when the request succeeded but produced nothing. */
  readonly empty?: ReactNode
  /** Decides whether the successful result counts as empty. */
  readonly isEmpty?: (data: T) => boolean
  readonly children: (data: T) => ReactNode
}

/**
 * Selects the right state for an async result.
 *
 * Centralised so a screen cannot accidentally render an error as an empty list, or an
 * empty list as a loading spinner that never resolves. The permission case is handled
 * here too, because a forbidden response is a state rather than a failure.
 */
export function AsyncBoundary<T>({
  isPending,
  error,
  data,
  onRetry,
  loadingLabel,
  empty,
  isEmpty,
  children,
}: AsyncBoundaryProps<T>) {
  if (isPending) {
    return (
      <LoadingState
        {...(loadingLabel === undefined ? {} : { label: loadingLabel })}
      />
    )
  }
  if (error !== null && error !== undefined) {
    if (
      error instanceof ApiError &&
      (error.kind === 'forbidden' || error.kind === 'notFound')
    ) {
      return <NoAccessState />
    }
    return (
      <ErrorState
        error={error}
        {...(onRetry === undefined ? {} : { onRetry })}
      />
    )
  }
  if (data === undefined) {
    return (
      <ErrorState
        error={new Error('No data was returned.')}
        {...(onRetry === undefined ? {} : { onRetry })}
      />
    )
  }
  if (empty !== undefined && isEmpty !== undefined && isEmpty(data)) {
    return <>{empty}</>
  }
  return <>{children(data)}</>
}
